import dayjs from 'dayjs'
import get from 'lodash/get'
import mapValues from 'lodash/mapValues'
import lowerCase from 'lodash/lowerCase'
import capitalize from 'lodash/capitalize'
import humps from 'humps'
import { Yup, createYupSchema } from '../../lib/validations'

// convert our dataTypes to yup types
const DATA_TYPES_MAPPER = {
  text: 'string',
  long_text: 'string',
  integer: 'number',
  float: 'decimal',
  date: 'date',
  bool: 'boolean',
  choice: 'string',
  multiple_choice: 'array',
}

function buildFieldValidations(fieldValidations = {}) {
  const validations = []

  if (fieldValidations.required) {
    validations.push({ type: 'required' })
  }

  if (fieldValidations.in) {
    validations.push({ type: 'min', params: [fieldValidations.in[0]] })
    validations.push({ type: 'max', params: [fieldValidations.in[1]] })
  }

  return validations
}

export const OTHER_OPTION_VALUE = 'Other'
export const OTHERS_SET_KEY = 'others'

export function getOptions(sets, formType) {
  if (!sets) return []
  return sets[formType] || sets.default
}

export function getFields(formSections = []) {
  return formSections.reduce((acc, section) => {
    section.fields.forEach(field => {
      acc[field.itemId] = field
    })
    return acc
  }, {})
}

export function getLabels(formSections) {
  return mapValues(getFields(formSections), value =>
    value.itemLabel === 'unlabeled'
      ? undefined
      : value.itemLabel || capitalize(lowerCase(value.itemName)),
  )
}

export function getPlaceholders(formSections) {
  const fields = getFields(formSections)

  return mapValues(fields, (_value, key) => {
    if (fields[key].dataType === 'date') return 'Select date'
    if (fields[key].dataType === 'choice') return 'Select an option'

    return ''
  })
}

const getMonthsAsYears = num => {
  const float = parseFloat(num / 12).toFixed(1)
  return float % 1 === 0 ? parseInt(float, 10) : float
}

function numberToDate(num) {
  // A number with decimals means years
  if (num % 1 !== 0) return `${num} year${num === 1 ? '' : 's'}`

  if (num > 11) {
    // Convert months to years
    const years = getMonthsAsYears(num)
    return `${years} year${years === 1 ? '' : 's'}`
  }

  // Less than 12, it's in months
  return `${num} month${num === 1 ? '' : 's'}`
}

export function formatBatteryValue(val) {
  if (!val) return ''

  const str = String(val).trim().toLowerCase()

  // does not change value when it contains month(s) or year(s)
  if (/^(\d*?\.?\d*) (year(s?)|month(s?))$/.test(str)) {
    return val
  }

  // does not change value when it is a percentage number
  if (/^\d*?\.?\d*%$/.test(str)) {
    return val
  }

  const num = Number(val)

  if (!Number.isNaN(num) && num >= 0) {
    // field is a number, add "months" or "years" to the number
    return numberToDate(num)
  }

  return String(val)
}

function strictDecamelize(str) {
  // insert underscores between letter-digit and digit-letter boundaries
  const patched = str.replace(/([a-zA-Z])([0-9])/g, '$1_$2') // e.g., vt1 → vt_1

  return humps.decamelize(patched)
}

export function formatInitialValues(
  fieldValues,
  formSections,
  formType,
  rawReport,
) {
  const fields = getFields(formSections)
  const others = {}

  const hasFieldValues = fieldValues && Object.keys(fieldValues).length > 0

  const metadataValues = rawReport?.deviceReportMetadata || {}

  const mappedMetadataValues = Object.entries(metadataValues).reduce(
    (acc, [key, value]) => {
      const snakeCaseKey = strictDecamelize(key)

      const matchingField = Object.values(fields).find(
        field => field.itemName === snakeCaseKey,
      )
      if (matchingField) {
        acc[matchingField.itemId] = value
      }
      return acc
    },
    {},
  )

  const valuesToUse = hasFieldValues ? fieldValues : mappedMetadataValues

  const formatValues = mapValues(fields, (field, key) => {
    const value = valuesToUse[key]
    const dataType = get(field, 'dataType')
    if (dataType === 'integer' && Number.isInteger(value)) return value

    if ([0, '0'].includes(value)) return value

    if (!value) return undefined

    if (dataType === 'date') return dayjs(value)
    if (dataType === 'choice') {
      const options = getOptions(field.options, formType)
      if (options.includes(value)) return value

      others[key] = value || ''
      return OTHER_OPTION_VALUE
    }

    return value
  })

  return { ...formatValues, [OTHERS_SET_KEY]: others }
}

export function formatSavedValues(values, formSections) {
  const { [OTHERS_SET_KEY]: others, ...restValues } = values
  const fields = getFields(formSections)
  const formatValues = mapValues(restValues, (value, key) => {
    if (get(fields, [key, 'itemName']) === 'estimated_battery_longevity') {
      return formatBatteryValue(value)
    }
    const dataType = get(fields, [key, 'dataType'])
    if (dataType === 'date' && value) {
      return typeof value === 'string' ? value : value.toISOString()
    }

    if (dataType === 'choice' && value === OTHER_OPTION_VALUE) {
      return others[key]
    }

    return value
  })

  const response = Object.entries(formatValues).map(([itemId, value]) => ({
    itemId,
    value,
    dataType: fields[itemId].dataType,
  }))

  return response
}

export function buildValidationSchema(formSections) {
  const fields = getFields(formSections)
  const schema = Object.entries(fields)
    .map(([key, field]) => ({
      id: key,
      validationType: DATA_TYPES_MAPPER[field.dataType],
      validations: buildFieldValidations(field.validations),
      label: capitalize(lowerCase(field.itemName)),
    }))
    .reduce(createYupSchema, {})

  return Yup.object().shape(schema)
}
